/** Theme catalog — CSS vars are RGB channels for Tailwind `rgb(var(--x) / <alpha>)`. */

const bluePrimary = {
  50: '239 246 255',
  100: '219 234 254',
  200: '191 219 254',
  300: '147 197 253',
  400: '96 165 250',
  500: '59 130 246',
  600: '37 99 235',
  700: '29 78 216',
  800: '30 64 175',
  900: '30 58 138',
  950: '23 37 84',
};

const skyPrimary = {
  50: '240 249 255',
  100: '224 242 254',
  200: '186 230 253',
  300: '125 211 252',
  400: '56 189 248',
  500: '14 165 233',
  600: '2 132 199',
  700: '3 105 161',
  800: '7 89 133',
  900: '12 74 110',
  950: '8 47 73',
};

const emeraldPrimary = {
  50: '236 253 245',
  100: '209 250 229',
  200: '167 243 208',
  300: '110 231 183',
  400: '52 211 153',
  500: '16 185 129',
  600: '5 150 105',
  700: '4 120 87',
  800: '6 95 70',
  900: '6 78 59',
  950: '2 44 34',
};

const amberPrimary = {
  50: '255 251 235',
  100: '254 243 199',
  200: '253 230 138',
  300: '252 211 77',
  400: '251 191 36',
  500: '245 158 11',
  600: '217 119 6',
  700: '180 83 9',
  800: '146 64 14',
  900: '120 53 15',
  950: '69 26 3',
};

const cyanPrimary = {
  50: '236 254 255',
  100: '207 250 254',
  200: '165 243 252',
  300: '103 232 249',
  400: '34 211 238',
  500: '6 182 212',
  600: '8 145 178',
  700: '14 116 144',
  800: '21 94 117',
  900: '22 78 99',
  950: '8 51 68',
};

const tealPrimary = {
  50: '240 253 250',
  100: '204 251 241',
  200: '153 246 228',
  300: '94 234 212',
  400: '45 212 191',
  500: '20 184 166',
  600: '13 148 136',
  700: '15 118 110',
  800: '17 94 89',
  900: '19 78 74',
  950: '4 47 46',
};

const indigoPrimary = {
  50: '238 242 255',
  100: '224 231 255',
  200: '199 210 254',
  300: '165 180 252',
  400: '129 140 248',
  500: '99 102 241',
  600: '79 70 229',
  700: '67 56 202',
  800: '55 48 163',
  900: '49 46 129',
  950: '30 27 75',
};

const zincNeutral = {
  50: '250 250 250',
  100: '244 244 245',
  200: '228 228 231',
  300: '212 212 216',
  400: '161 161 170',
  500: '113 113 122',
  600: '82 82 91',
  700: '63 63 70',
  800: '39 39 42',
  900: '24 24 27',
  950: '9 9 11',
};

const stoneNeutral = {
  50: '250 250 249',
  100: '245 245 244',
  200: '231 229 228',
  300: '214 211 209',
  400: '168 162 158',
  500: '120 113 108',
  600: '87 83 78',
  700: '68 64 60',
  800: '41 37 36',
  900: '28 25 23',
  950: '12 10 9',
};

const slateNeutral = {
  50: '248 250 252',
  100: '241 245 249',
  200: '226 232 240',
  300: '203 213 225',
  400: '148 163 184',
  500: '100 116 139',
  600: '71 85 105',
  700: '51 65 85',
  800: '30 41 59',
  900: '15 23 42',
  950: '2 6 23',
};

const oliveNeutral = {
  50: '247 248 246',
  100: '236 239 234',
  200: '220 226 216',
  300: '190 201 184',
  400: '140 154 132',
  500: '100 114 96',
  600: '74 86 70',
  700: '52 64 50',
  800: '32 42 32',
  900: '18 26 18',
  950: '8 12 9',
};

/** Near-pure black for OLED / deepest dark mode */
const oledNeutral = {
  50: '250 250 250',
  100: '244 244 245',
  200: '228 228 231',
  300: '212 212 216',
  400: '140 140 148',
  500: '100 100 108',
  600: '64 64 70',
  700: '38 38 44',
  800: '16 16 18',
  900: '6 6 8',
  950: '0 0 0',
};

/** Cool blue-gray graphite */
const graphiteNeutral = {
  50: '248 250 252',
  100: '241 245 249',
  200: '226 232 240',
  300: '203 213 225',
  400: '148 163 184',
  500: '100 116 139',
  600: '71 85 105',
  700: '45 58 74',
  800: '22 30 42',
  900: '12 16 24',
  950: '4 6 12',
};

/** Steel / bluish night */
const steelNeutral = {
  50: '246 248 252',
  100: '232 238 248',
  200: '210 220 236',
  300: '176 192 216',
  400: '120 140 172',
  500: '88 108 140',
  600: '64 82 112',
  700: '46 60 86',
  800: '28 38 56',
  900: '14 20 34',
  950: '6 10 18',
};

const paperNeutral = {
  50: '250 247 242',
  100: '243 236 226',
  200: '230 218 202',
  300: '209 192 170',
  400: '168 148 124',
  500: '130 112 90',
  600: '100 86 68',
  700: '78 66 52',
  800: '52 44 36',
  900: '36 30 24',
  950: '20 16 12',
};

const mistNeutral = {
  50: '244 247 251',
  100: '232 238 246',
  200: '210 220 234',
  300: '180 196 218',
  400: '132 152 180',
  500: '96 116 144',
  600: '72 90 116',
  700: '54 70 92',
  800: '36 48 68',
  900: '22 32 48',
  950: '10 14 24',
};

const toVars = (primary, neutral) => {
  const vars = {};
  Object.entries(primary).forEach(([k, v]) => {
    vars[`--color-primary-${k}`] = v;
  });
  Object.entries(neutral).forEach(([k, v]) => {
    vars[`--color-neutral-${k}`] = v;
  });
  return vars;
};

const previewFrom = (neutral, primary, mode) => ({
  bg: mode === 'dark' ? `rgb(${neutral[950]})` : `rgb(${neutral[50]})`,
  surface: mode === 'dark' ? `rgb(${neutral[900]})` : `rgb(${neutral[100]})`,
  border: mode === 'dark' ? `rgb(${neutral[700]})` : `rgb(${neutral[200]})`,
  accent: `rgb(${primary[500]})`,
});

export const DEFAULT_THEME_ID = 'midnight';

export const THEMES = {
  midnight: {
    id: 'midnight',
    name: 'Mitternacht',
    description: 'Klassisches Dunkel mit Blau',
    mode: 'dark',
    group: 'dark',
    vars: toVars(bluePrimary, zincNeutral),
    preview: previewFrom(zincNeutral, bluePrimary, 'dark'),
  },
  oled: {
    id: 'oled',
    name: 'Tiefschwarz',
    description: 'Reines Schwarz – dunkler als Mitternacht',
    mode: 'dark',
    group: 'dark',
    vars: toVars(bluePrimary, oledNeutral),
    preview: previewFrom(oledNeutral, bluePrimary, 'dark'),
  },
  charcoal: {
    id: 'charcoal',
    name: 'Anthrazit',
    description: 'Warmes Dunkel mit Amber',
    mode: 'dark',
    group: 'dark',
    vars: toVars(amberPrimary, stoneNeutral),
    preview: previewFrom(stoneNeutral, amberPrimary, 'dark'),
  },
  ocean: {
    id: 'ocean',
    name: 'Ozean',
    description: 'Kühles Slate mit Sky-Blau',
    mode: 'dark',
    group: 'dark',
    vars: toVars(skyPrimary, slateNeutral),
    preview: previewFrom(slateNeutral, skyPrimary, 'dark'),
  },
  forest: {
    id: 'forest',
    name: 'Wald',
    description: 'Grün getöntes Dunkel',
    mode: 'dark',
    group: 'dark',
    vars: toVars(emeraldPrimary, oliveNeutral),
    preview: previewFrom(oliveNeutral, emeraldPrimary, 'dark'),
  },
  graphite: {
    id: 'graphite',
    name: 'Graphit',
    description: 'Kühles Stahlgrau mit Cyan',
    mode: 'dark',
    group: 'dark',
    vars: toVars(cyanPrimary, graphiteNeutral),
    preview: previewFrom(graphiteNeutral, cyanPrimary, 'dark'),
  },
  steel: {
    id: 'steel',
    name: 'Stahl',
    description: 'Bläuliches Nachtgrau',
    mode: 'dark',
    group: 'dark',
    vars: toVars(skyPrimary, steelNeutral),
    preview: previewFrom(steelNeutral, skyPrimary, 'dark'),
  },
  light: {
    id: 'light',
    name: 'Hell',
    description: 'Klares Standard-Light',
    mode: 'light',
    group: 'light',
    vars: toVars(bluePrimary, zincNeutral),
    preview: previewFrom(zincNeutral, bluePrimary, 'light'),
  },
  paper: {
    id: 'paper',
    name: 'Papier',
    description: 'Warmes Off-White mit Teal',
    mode: 'light',
    group: 'light',
    vars: toVars(tealPrimary, paperNeutral),
    preview: previewFrom(paperNeutral, tealPrimary, 'light'),
  },
  mist: {
    id: 'mist',
    name: 'Nebel',
    description: 'Kühles Hellgrau mit Indigo',
    mode: 'light',
    group: 'light',
    vars: toVars(indigoPrimary, mistNeutral),
    preview: previewFrom(mistNeutral, indigoPrimary, 'light'),
  },
};

export const THEME_LIST = Object.values(THEMES);
export const DARK_THEMES = THEME_LIST.filter((t) => t.group === 'dark');
export const LIGHT_THEMES = THEME_LIST.filter((t) => t.group === 'light');

const LEGACY_THEME_MAP = {
  dark: 'midnight',
  light: 'light',
  ember: 'oled',
};

export function resolveThemeId(raw) {
  if (!raw) return DEFAULT_THEME_ID;
  const mapped = LEGACY_THEME_MAP[raw] || raw;
  return THEMES[mapped] ? mapped : DEFAULT_THEME_ID;
}

export function getTheme(id) {
  return THEMES[resolveThemeId(id)];
}

export function applyThemeToDocument(themeId) {
  const theme = getTheme(themeId);
  const root = document.documentElement;
  root.setAttribute('data-theme', theme.id);

  Object.entries(theme.vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });

  if (theme.mode === 'dark') {
    root.classList.add('dark');
    root.style.colorScheme = 'dark';
  } else {
    root.classList.remove('dark');
    root.style.colorScheme = 'light';
  }

  return theme;
}
